import React, { Suspense, useEffect, useState } from 'react'
import { Global, css } from '@emotion/core'
import RebuggerClient from '@fs/zion-rebugger/AppClient'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { NoticeLoading } from '@fs/zion-icon'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout, Theme, colors } from '@fs/zion-ui'
import { Offline } from '@fs/zion-error-page'
import { Router, useLocation } from '@fs/zion-router'
import { useIsHelping } from '@fs/zion-user'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { setAppLocales } from '@fs/zion-locale'
import supportedLocales from './supportedLocales'
import AppRootProvider, { useAppState } from './providers/AppRootProvider'
import App from './App'
import { init as initBraze } from './braze'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { usePWAMode, useOffline } from './hooks'
import DarkModeProvider, { useDarkModeContext } from './providers/DarkModeProvider'
import { isFooterVisiblePath } from './helpers/routes'
import getIsTWAMode from './helpers/appMode_helper'
import { captureTwaNotificationState, captureDiscoveryCodeLaunchParams } from './helpers/twaNotifications'
import getLoadingFallback, { LoadingHold } from './helpers/loadingFallback'
import './locales'

// Capture the native-reported notification state from the launch URL before the router strips the query string.
captureTwaNotificationState()
captureDiscoveryCodeLaunchParams()

const Usabilla = React.lazy(() => import('@fs/zion-usabilla').then((module) => ({ default: module.Usabilla })))

const tapHighlightCss = css`
  html {
    -webkit-tap-highlight-color: transparent;
  }
`

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode & use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        // Browser page translation swaps React's text nodes for <font> elements. https://github.com/facebook/react/issues/11538
        `Failed to execute 'removeChild' on 'Node'`,
        `Failed to execute 'insertBefore' on 'Node'`,
        'NotFoundError: The object can not be found here.', // Safari wording of the two errors above
      ],
    })
  )
}

// Initialize Braze once. Internal guard prevents double init
initBraze().catch((e) => {
  // eslint-disable-next-line no-console
  console.warn('Braze init failed', e)
})

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const flagOptions = getIsTWAMode() ? { LoadingFallback: LoadingHold } : undefined

const handleBeforeInstallPrompt = (event) => {
  // Store this event for later use
  event.preventDefault()
  window.deferredPrompt = event
}

const AppRoot = () => {
  const PWAMode = usePWAMode()
  const isTWAMode = getIsTWAMode()
  const { isDarkMode } = useDarkModeContext()
  const isHelping = useIsHelping()
  const [open, setOpen] = useState(isHelping)
  const { footer } = useAppState()
  const location = useLocation()

  // Pre-determine if footer should be hidden based on route pattern
  // This prevents flash when navigating to activity routes before useFooterHidden runs
  const isFooterVisibleRoute = isFooterVisiblePath(location.pathname)
  const footerConfig = PWAMode || !isFooterVisibleRoute ? { hidden: true } : footer
  const [t] = useTranslation()
  const isOffline = useOffline()

  const alertBannerProps = {
    message: t(
      'helper.mode.not.supported',
      'Helper Mode is not supported. Please exit helper mode before using this application.'
    ),
    dismissText: t('common-ui:dismiss.label.action', 'Dismiss'),
    onConfirm: () => {},
    open,
    setOpen,
    type: 'warning',
  }

  useEffect(() => {
    setOpen(isHelping)
  }, [isHelping])

  // prevent right-click/tap-hold context menu when in Android TWA (maybe we can implement a custom context menu?)
  PWAMode && window.addEventListener('contextmenu', (e) => e.preventDefault())

  useEffect(() => {
    // If you want your app to work offline and load faster, you can change unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://cra.link/PWA
    if ('serviceWorker' in navigator) {
      serviceWorkerRegistration.register(`${window.SERVER_DATA.appPath}/service-worker.js`) // Register the messaging service worker in all cases
    }
  }, [PWAMode])

  return (
    <Root
      alertBannerProps={alertBannerProps}
      footer={footerConfig}
      header={{ hidden: PWAMode }}
      analytics
      helpChatbot={false}
    >
      {/* colors.gray100 isn't consistent here as it's outside of <Theme /> */}
      <AppLayout fullWidth backgroundColor={isDarkMode ? '#0d0f10' : colors.gray00}>
        <Theme theme={isDarkMode ? 'night' : 'day'}>
          {isTWAMode && <Global styles={tapHighlightCss} />}
          {isOffline && PWAMode ? (
            <Offline />
          ) : (
            <>
              <App />
              <Usabilla hideOnMobile mobileBreakpoint="xxl" url="//w.usabilla.com/e646f09e862d.js" />
            </>
          )}
        </Theme>
      </AppLayout>
    </Root>
  )
}

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  setAppLocales(supportedLocales)

  return (
    <Router basename={base}>
      <DarkModeProvider>
        <AppRootProvider>
          <AppRoot />
        </AppRootProvider>
      </DarkModeProvider>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={getLoadingFallback(<NoticeLoading />)}>
        <FrontierBaseProviders flagOptions={flagOptions} mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
      {process.env.NODE_ENV === 'development' && <RebuggerClient />}
    </React.StrictMode>
  )
}
window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))
